import { api } from './client'

/**
 * F-076 (TASK-0330): client della scheda cliente 360 (`ContactOverviewController`,
 * `GET /api/contacts/{id}/overview`), dell'attività da suggerimento
 * (`POST /api/contacts/{id}/cross-sell/{ruleId}/task`) e delle "Prossime azioni"
 * (`GET /api/next-best-actions/me`). Nessun importo: il server non ne invia, quindi i tipi non li hanno.
 * Il cliente è un contatto (`crm.contacts`), non un record di `crm.customers`.
 */

export type ActionPriority = 'Alta' | 'Media' | 'Bassa'

export type ActionKind =
  | 'richiama'
  | 'completa_pratica'
  | 'cross_sell'
  | 'documento_in_scadenza'
  | 'prodotto_in_scadenza'
  | 'riprogramma_appuntamento'

export interface ContactCard {
  id: number
  name: string
  contactType: string | null
  status: string
  email: string | null
  phone: string | null
  mobile: string | null
  fiscalCode: string | null
  vatNumber: string | null
  assignedAgentId: number | null
  assignedAgentName: string | null
  /** Valorizzato solo se questo contatto è stato fuso in un altro: la scheda vera è quella. */
  mergedIntoContactId: number | null
  /** F-108: valorizzati se il cliente e' nel cestino (la scheda si legge ancora; le scritture sono rifiutate). */
  deletedAt: string | null
  deletedBy: number | null
  deleteReason: string | null
  /** Valore del sistema precedente (NUMBERS, SEVEN…): NON è una fonte di acquisizione né un first touch. */
  registeredSource: { value: string | null; label: string; note: string }
}

export interface ContactTimelineItem {
  /** Istante UTC (ISO 8601). */
  occurredAt: string
  kind: string
  title: string
  actorUserId: number | null
  actorName: string | null
  refType: string | null
  refId: number | null
  contractId: number | null
  /** True solo per la fotografia dello stato al caricamento dal sistema precedente (non è un evento). */
  isSnapshot: boolean
}

export interface AttributionTouch {
  leadId: number
  createdAt: string
  source: string | null
  campaignId: number | null
  supplier: string | null
  channel: string | null
  utmSource: string | null
  utmMedium: string | null
  utmCampaign: string | null
}

export interface ContactAttribution {
  first: AttributionTouch | null
  last: AttributionTouch | null
  latestContractAt: string | null
  notes: string[]
}

export interface ContactContract {
  id: number
  referenceCode: string
  status: string
  departmentId: number | null
  departmentName: string | null
  productId: number | null
  productName: string | null
  createdAt: string
  signedAt?: string | null
}

export interface ContactHoldings {
  /** Le pratiche su cui sono calcolati gli elenchi: quelle visibili al chiamante. */
  scope: string
  departments: { departmentId: number; name: string; contracts: number }[]
  products: { productId: number; name: string; contracts: number }[]
}

export interface CrossSellSuggestion {
  ruleId: number
  ruleName: string
  label: string
  priority: ActionPriority
  reason: string
}

export interface NextAction {
  kind: ActionKind
  priority: ActionPriority
  title: string
  reason: string
  refType: string | null
  refId: number | null
  contractId: number | null
  ruleId: number | null
}

export interface ContactOverview {
  contact: ContactCard
  timeline: { items: ContactTimelineItem[]; truncated: boolean }
  attribution: ContactAttribution
  contracts: ContactContract[]
  holdings: ContactHoldings
  crossSell: CrossSellSuggestion[]
  actions: NextAction[]
}

export interface CrossSellTaskResult {
  created: boolean
  taskId: number
  assignedToId: number | null
}

export interface MyNextAction extends NextAction {
  contactId: number
  contactName: string
}

export interface MyNextActions {
  total: number
  contactsConsidered: number
  /** True se i clienti dell'agente superano il tetto per chiamata. */
  contactsTruncated: boolean
  items: MyNextAction[]
}

export const contactOverviewApi = {
  get: (id: number) => api<ContactOverview>(`/api/contacts/${id}/overview`),
  createCrossSellTask: (contactId: number, ruleId: number) =>
    api<CrossSellTaskResult>(`/api/contacts/${contactId}/cross-sell/${ruleId}/task`, { method: 'POST' }),
  myNextActions: (limit = 50) => api<MyNextActions>(`/api/next-best-actions/me?limit=${limit}`),
}

/**
 * F-156 (TASK-0410, REQ-022-001): archivio del cliente ad albero (`GET /api/contacts/{id}/archive`, sola lettura).
 * Cliente → documenti del cliente → pratiche visibili → allegati → comunicazioni. Mai contenuto dei file, percorsi
 * di archiviazione né importi: il file si apre da `/api/documents/{id}/content`. Un cliente non visibile è 404.
 */
export interface ArchiveDocument {
  id: number
  name: string
  type: string | null
  mimeType: string
  createdAt: string
  /** Completato | InCoda | Errore (F-141). */
  uploadStatus: string
  /** True se il documento è stato generato da un modello (non caricato). */
  generated: boolean
}

export interface ArchiveContract {
  id: number
  referenceCode: string
  status: string
  departmentName: string | null
  createdAt: string
  signedAt?: string | null
  documents: ArchiveDocument[]
}

export interface ArchiveCall {
  id: number
  leadId: number
  occurredAt: string
  outcome: string | null
  operatorName: string | null
}

export interface ArchiveAppointment {
  id: number
  startAt: string
  status: string
}

export interface ContactArchive {
  contact: { id: number; name: string }
  customerDocuments: ArchiveDocument[]
  contracts: ArchiveContract[]
  communications: {
    calls: ArchiveCall[]
    appointments: ArchiveAppointment[]
    truncated: boolean
    /** Canali non ancora tracciati nel CRM (E2): email, SMS, WhatsApp. */
    missingChannels: string[]
    note: string
  }
}

export const contactArchiveApi = {
  get: (id: number) => api<ContactArchive>(`/api/contacts/${id}/archive`),
}
